// Exchanges v2 — THE ADVISOR'S ITEMS, ONE LINE EACH (Astra: "Hide long advice"; the pack flagged advice sentences over the
// word budget). The same read and the same split as the old screen: what is wrong now ("fix") stands open; what is only
// advised or noted folds behind "More (N)". Each item is its title on one line; the action, the detail and the rule book
// are behind the item's own tap. A note's "Nothing to do" filler is still left out. "Nothing to fix" is said only from a
// read that succeeded — after a failed refresh the list is the last good read, and its head says so.
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { CexAdviceItem } from '../../../lib/api';
import { Pill } from '../../kit';
import { Panel, ReadFailed } from '../kit';
import { MoneyText } from '../MoneyText';

export function AdvicePanel({ items, failed, error, onRetry }: {
  items: CexAdviceItem[];
  failed: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (k: string) => setOpen((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const fix = items.filter((i) => i.severity === 'fix');
  const rest = items.filter((i) => i.severity !== 'fix');
  const shown = [...fix, ...(moreOpen ? rest : [])];
  if (!failed && items.length === 0) return null;

  return (
    <div id="advice" className="mb-4 space-y-3 lg:mb-5">
      {/* A failed advice read is amber: the screen can live without it (the old screen's tone). */}
      {failed && <ReadFailed what="the advice" error={error} onRetry={onRetry} />}
      {items.length > 0 && (
        <Panel
          flush
          title={fix.length > 0
            ? <span className="text-neg">{fix.length} thing{fix.length > 1 ? 's' : ''} to fix</span>
            : failed ? <span className="text-tg-hint">Last read</span> : 'Nothing to fix'}
          action={rest.length > 0 && (
            <button type="button" className="tap-44 btn-link text-body" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
              {moreOpen ? 'Fewer' : `More (${rest.length})`}
            </button>
          )}
        >
          {shown.length > 0 && (
            <ul>
              {shown.map((i) => {
                const k = i.subject + i.rule;
                const act = i.action && !/^nothing to do\b/i.test(i.action.trim()) ? i.action.trim() : '';
                // A THING TO FIX SAYS WHAT TO DO, open (the audit, 1 Nov 2026: its action was folded behind the tap); the detail
                // and the rule book stay behind it.
                const actOpen = i.severity === 'fix' && !!act;
                const more = !!((act && !actOpen) || i.detail || i.book);
                const isOpen = more && open.has(k);
                const head = (
                  <>
                    {i.severity !== 'fix' && (
                      <Pill tone={i.severity === 'advise' ? 'warn' : 'neutral'} className="shrink-0">{i.severity === 'advise' ? 'Advised' : 'Noted'}</Pill>
                    )}
                    <span className={`min-w-0 flex-1 text-body font-medium ${isOpen ? 'break-words' : 'truncate'}`} title={i.title}><MoneyText text={i.title} /></span>
                    {more && <ChevronDown aria-hidden className={`h-4 w-4 shrink-0 text-tg-hint transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
                  </>
                );
                return (
                  <li key={k} className="border-b border-stroke-soft last:border-b-0">
                    {more ? (
                      <button type="button" aria-expanded={isOpen} onClick={() => toggle(k)}
                        className="flex min-h-[44px] w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-panel-2 lg:min-h-[40px] lg:px-5">
                        {head}
                      </button>
                    ) : (
                      <div className="flex min-h-[44px] items-center gap-2 px-4 py-2.5 lg:min-h-[40px] lg:px-5">{head}</div>
                    )}
                    {actOpen && <p className="-mt-1 break-words px-4 pb-3 text-body text-tg-text lg:px-5">{act}</p>}
                    {isOpen && (
                      <div className="space-y-1 px-4 pb-3 text-meta lg:px-5">
                        {act && !actOpen && <p className="break-words text-tg-text">{act}</p>}
                        {i.detail && <p className="break-words text-tg-hint">{i.detail}</p>}
                        {i.book && <p className="text-tg-hint">Rule book {i.book}</p>}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      )}
    </div>
  );
}
